import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import {
  EntityTypesSupportingDownloading,
  EntityTypeSupportingGroups,
  Group,
} from "@mapmycustomers/shared/types/entity";
import { MultiPin } from "@mapmycustomers/shared/types/map";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { EditableText } from "@mapmycustomers/ui";

import DownloadEntitiesModal from "@app/component/DownloadEntitiesModal";
import {
  downloadGroupRecords,
  exitGroupMode,
  updateGroup,
} from "@app/scene/map/store/groupMode/actions";
import {
  getGroup,
  getGroupEntityType,
  getMapViewTool,
  getMultiPin,
} from "@app/scene/map/store/selectors";
import { deleteGroup } from "@app/store/groups/actions";
import { RootState } from "@app/store/rootReducer";

import GroupMenu from "../GroupMenu";

import styles from "./Title.module.scss";

const messages = defineMessages({
  multiPin: {
    id: "map.recordsList.groupMode.title",
    defaultMessage: "Multiple Records",
    description: "Records list for multi-pin - title",
  },
});

interface Props {
  entityType?: EntityTypeSupportingGroups;
  group?: Group;
  multiPin?: MultiPin;
  onDeleteGroup: typeof deleteGroup.request;
  onDownloadGroupRecords: typeof downloadGroupRecords;
  onExitGroupMode: typeof exitGroupMode.request;
  onUpdateGroup: typeof updateGroup.request;
  selectedTool?: MapTool;
}

const Title: React.FC<Props> = ({
  entityType,
  group,
  multiPin,
  onDeleteGroup,
  onDownloadGroupRecords,
  onExitGroupMode,
  onUpdateGroup,
  selectedTool,
}) => {
  const intl = useIntl();
  const [renaming, setRenaming] = useState(false);
  const [visible, show, hide] = useBoolean();
  const handleRename = useCallback(() => setRenaming(true), []);

  const isMultiPinMode = !!multiPin && selectedTool !== MapTool.LASSO;

  const handleNameChange = useCallback(
    (name: string) => {
      if (group) {
        onUpdateGroup?.({
          group: { ...group, name },
        });
      }
    },
    [group, onUpdateGroup]
  );

  const handleDeleteGroup = useCallback(() => {
    if (!group || !entityType) {
      return;
    }
    onDeleteGroup?.({ entityType, groupId: group.id });
    onExitGroupMode();
  }, [group, entityType, onDeleteGroup, onExitGroupMode]);

  const handleDownload = useCallback(() => {
    if (!group || !entityType) {
      return;
    }
    show();
  }, [group, entityType, show]);

  return isMultiPinMode ? (
    <>{intl.formatMessage(messages.multiPin)}</>
  ) : (
    <div className={styles.titleContainer}>
      <span className={styles.title}>
        <Tooltip title={group?.name}>
          <EditableText
            editable={!!group?.accessStatus.update}
            editing={renaming}
            fieldClassName={styles.nameField}
            onChange={handleNameChange}
            onEditingChange={setRenaming}
            required
            size="default"
            tooltipTitle={intl.formatMessage({
              id: "map.groups.sidebar.title.tooltip",
              defaultMessage: "Change Group Name",
              description: "Group name tooltip on the View Group page",
            })}
            value={group?.name ?? ""}
          />
        </Tooltip>
      </span>
      <GroupMenu
        group={group}
        onDelete={handleDeleteGroup}
        onDownload={handleDownload}
        onRename={handleRename}
      />
      {visible && (
        <DownloadEntitiesModal
          entityType={entityType as EntityTypesSupportingDownloading}
          onDownload={onDownloadGroupRecords}
          onHide={hide}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  entityType: getGroupEntityType(state),
  group: getGroup(state),
  multiPin: getMultiPin(state),
  selectedTool: getMapViewTool(state),
});

const mapDispatchToProps = {
  onDeleteGroup: deleteGroup.request,
  onDownloadGroupRecords: downloadGroupRecords,
  onExitGroupMode: exitGroupMode.request,
  onUpdateGroup: updateGroup.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Title);
